/**
 * AuditoriaModal — muestra creación, última modificación e historial de cambios.
 *
 * Uso:
 *   <AuditoriaRegistroBtn registro={factura} />
 *
 * El botón solo se muestra si el usuario tiene permiso "auditoria.ver_info_registro".
 */
import { useMemo, useState, useContext } from "react";
import { createPortal } from "react-dom";
import { Activity, Clock3, Info, Search, ShieldCheck, UserRound, Wallet, X } from "lucide-react";
import { AuthContext } from "../App";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-PY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function clean(v) {
  return (v || "").toString().trim();
}

function auditFrom(registro) {
  return registro?._auditoria || registro?.auditoria || {};
}

function buildHistory(a) {
  const historial = Array.isArray(a.historial) ? [...a.historial] : [];
  if (historial.length === 0 && a.creado_en) {
    historial.push({
      tipo: "creacion",
      fecha: a.creado_en,
      detalle: "Registro creado",
      usuario: a.creado_por,
      usuario_id: a.creado_por_id,
      usuario_email: a.creado_por_email,
      usuario_role: a.creado_por_role,
    });
  }
  if (a.modificado_en && !historial.some((h) => h.fecha === a.modificado_en && h.tipo === "modificacion")) {
    historial.push({
      tipo: "modificacion",
      fecha: a.modificado_en,
      detalle: "Última modificación",
      usuario: a.modificado_por,
      usuario_id: a.modificado_por_id,
      usuario_email: a.modificado_por_email,
      usuario_role: a.modificado_por_role,
    });
  }
  return historial.sort((aItem, bItem) => new Date(bItem.fecha || 0) - new Date(aItem.fecha || 0));
}

function InfoCard({ title, user, date, email, role, icon: Icon }) {
  return (
    <div className="rounded-xl border border-arandu-border bg-arandu-dark/60 p-4">
      <div className="flex items-center gap-2 text-arandu-text-muted text-xs font-medium uppercase tracking-wide">
        <Icon className="w-4 h-4 text-arandu-accent" />
        {title}
      </div>
      <p className="mt-3 text-arandu-text font-semibold">{user || "—"}</p>
      <p className="text-arandu-text-muted text-xs">{fmt(date)}</p>
      {(email || role) && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          {email && <span className="rounded-full bg-white/5 px-2 py-1 text-arandu-text-muted">{email}</span>}
          {role && <span className="rounded-full bg-arandu-accent/10 px-2 py-1 text-arandu-accent">{role}</span>}
        </div>
      )}
    </div>
  );
}

export function AuditoriaRegistroBtn({ registro, className = "" }) {
  const { hasPermission } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const a = auditFrom(registro);
  const historial = useMemo(() => buildHistory(a), [a]);
  const filtrado = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return historial;
    return historial.filter((h) => [
      h.tipo,
      h.detalle,
      h.usuario,
      h.usuario_email,
      h.usuario_role,
      fmt(h.fecha),
    ].some((v) => clean(v).toLowerCase().includes(q)));
  }, [historial, query]);

  if (!hasPermission("auditoria.ver_info_registro")) return null;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Ver info de registro"
        className={`inline-flex items-center justify-center w-7 h-7 rounded text-arandu-text-muted hover:text-arandu-accent hover:bg-arandu-dark-light transition-colors ${className}`}
      >
        <Clock3 className="w-4 h-4" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/65 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-arandu-dark-light border border-arandu-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[86vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-arandu-border px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-arandu-accent/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-arandu-accent" />
                </div>
                <div>
                  <h3 className="text-arandu-text font-semibold">Información del registro</h3>
                  <p className="text-arandu-text-muted text-xs">Creación, última modificación e historial disponible</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-lg text-arandu-text-muted hover:text-arandu-text hover:bg-white/5 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(86vh-74px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoCard
                  title="Creación"
                  user={a.creado_por}
                  date={a.creado_en}
                  email={a.creado_por_email}
                  role={a.creado_por_role}
                  icon={UserRound}
                />
                <InfoCard
                  title="Última modificación"
                  user={a.modificado_por}
                  date={a.modificado_en}
                  email={a.modificado_por_email}
                  role={a.modificado_por_role}
                  icon={Activity}
                />
                {a.pago_por && (
                  <InfoCard
                    title="Último pago / cobro registrado por"
                    user={a.pago_por}
                    date={a.pago_en}
                    email={a.pago_por_email}
                    role={a.pago_por_role}
                    icon={Wallet}
                  />
                )}
              </div>

              <div className="mt-5 rounded-xl border border-arandu-border bg-arandu-dark/35">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-arandu-border px-4 py-3">
                  <div>
                    <p className="text-arandu-text text-sm font-semibold">Historial de cambios</p>
                    <p className="text-arandu-text-muted text-xs">{historial.length} evento{historial.length !== 1 ? "s" : ""} registrado{historial.length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="relative sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-arandu-text-muted" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Buscar evento..."
                      className="w-full rounded-lg border border-arandu-border bg-arandu-dark py-2 pl-9 pr-3 text-sm text-arandu-text placeholder-arandu-text-muted focus:outline-none focus:border-arandu-accent/60"
                    />
                  </div>
                </div>

                <div className="divide-y divide-arandu-border">
                  {filtrado.length === 0 ? (
                    <div className="flex items-center gap-2 px-4 py-8 text-sm text-arandu-text-muted">
                      <Info className="w-4 h-4" />
                      Sin eventos para mostrar.
                    </div>
                  ) : filtrado.map((h, idx) => (
                    <div key={`${h.fecha || idx}-${idx}`} className="px-4 py-3">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              h.tipo === "creacion"
                                ? "bg-emerald-500/12 text-emerald-300"
                                : h.tipo === "pago"
                                  ? "bg-sky-500/12 text-sky-300"
                                  : ["pago_anulado", "anulacion", "eliminacion"].includes(h.tipo)
                                    ? "bg-rose-500/12 text-rose-300"
                                    : "bg-violet-500/12 text-violet-300"
                            }`}>
                              {{ creacion: "Creación", pago: "Pago / Cobro", pago_anulado: "Pago anulado", anulacion: "Anulación", eliminacion: "Eliminación" }[h.tipo] || "Modificación"}
                            </span>
                            <span className="text-arandu-text text-sm font-medium">{h.detalle || "Registro actualizado"}</span>
                          </div>
                          <p className="mt-1 text-arandu-text-muted text-xs">
                            {h.usuario || "Usuario no identificado"}
                            {h.usuario_email ? ` · ${h.usuario_email}` : ""}
                            {h.usuario_role ? ` · ${h.usuario_role}` : ""}
                          </p>
                        </div>
                        <p className="text-arandu-text-muted text-xs whitespace-nowrap">{fmt(h.fecha)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
